/**
 * Character factory and validation for Idle Depths.
 *
 * Provides functions to create new characters with default values
 * and validate character data for import.
 *
 * Note: This file uses the deprecated stats registry for backward compatibility.
 * New code should use the content pack system instead.
 */

/* eslint-disable @typescript-eslint/no-deprecated */

import { createIdleActivity, DEFAULT_STARTING_LOCATION } from '../types/character.js';
import { createEmptyEquipment } from '../types/item.js';
import { isSkillProgress } from '../types/skill.js';

import { STARTING_CURRENCY } from './items.js';
import { initializeSkills } from './skills.js';
import { getAllStatDefinitions } from './stats.js';

import type { CharacterData, CharacterExport, CurrentActivity } from '../types/character.js';
import type { SkillProgress } from '../types/skill.js';
import type { CharacterStat } from '../types/stats.js';

/**
 * Create default stats for a new character.
 *
 * Initializes all registered stats with their default values.
 *
 * @returns Record of stat ID to CharacterStat
 */
export function createDefaultStats(): Record<string, CharacterStat> {
  const stats: Record<string, CharacterStat> = {};

  for (const definition of getAllStatDefinitions()) {
    stats[definition.id] = {
      statId: definition.id,
      currentValue: definition.defaultValue,
      maximumValue: definition.defaultMaximum,
    };
  }

  return stats;
}

/**
 * Create a new character with default values.
 *
 * @param id - Character ID (generated externally with nanoid)
 * @param name - Character name (player-chosen)
 * @param slotIndex - Slot index for ordering
 * @returns A new CharacterData object with defaults
 */
export function createCharacter(id: string, name: string, slotIndex: number): CharacterData {
  const now = Date.now();

  return {
    id,
    name,
    slotIndex,
    createdAt: now,
    updatedAt: now,
    lastPlayedAt: now,
    totalPlayTime: 0,
    currentLocationId: DEFAULT_STARTING_LOCATION,
    stats: createDefaultStats(),
    currentActivity: createIdleActivity(),
    skills: initializeSkills(),
    currency: STARTING_CURRENCY,
    equipment: createEmptyEquipment(),
    lastSyncedAt: null,
    needsSync: true,
  };
}

/**
 * Validate that an object is valid CharacterData.
 *
 * Used when importing characters to ensure data integrity.
 *
 * @param data - Unknown data to validate
 * @returns True if data is valid CharacterData
 */
export function validateCharacterData(data: unknown): data is CharacterData {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const character = data as Record<string, unknown>;

  // Required string fields
  if (
    typeof character.id !== 'string' ||
    typeof character.name !== 'string' ||
    typeof character.currentLocationId !== 'string'
  ) {
    return false;
  }

  // Required number fields
  if (
    typeof character.slotIndex !== 'number' ||
    typeof character.createdAt !== 'number' ||
    typeof character.updatedAt !== 'number' ||
    typeof character.lastPlayedAt !== 'number' ||
    typeof character.totalPlayTime !== 'number'
  ) {
    return false;
  }

  // Name length validation
  if (character.name.length < 3 || character.name.length > 20) {
    return false;
  }

  // Stats validation
  if (typeof character.stats !== 'object' || character.stats === null) {
    return false;
  }

  const stats = character.stats as Record<string, unknown>;
  for (const stat of Object.values(stats)) {
    if (!validateCharacterStat(stat)) {
      return false;
    }
  }

  // Current activity validation
  if (!validateCurrentActivity(character.currentActivity)) {
    return false;
  }

  // Currency (optional for backward compatibility with pre-v3 data)
  // If currency exists, it must be a non-negative number
  if (character.currency !== undefined && typeof character.currency !== 'number') {
    return false;
  }
  if (typeof character.currency === 'number' && character.currency < 0) {
    return false;
  }

  // Equipment (optional for backward compatibility with pre-v3 data)
  // If equipment exists, it must be an object with valid slot values
  if (character.equipment !== undefined) {
    if (typeof character.equipment !== 'object' || character.equipment === null) {
      return false;
    }
    const equipment = character.equipment as Record<string, unknown>;
    for (const value of Object.values(equipment)) {
      if (value !== null && typeof value !== 'string') {
        return false;
      }
    }
  }

  // Skills validation (optional for backward compatibility with pre-v5 data)
  // If skills exists, validate each skill progress
  if (character.skills !== undefined) {
    if (typeof character.skills !== 'object' || character.skills === null) {
      return false;
    }
    for (const skillProgress of Object.values(character.skills)) {
      if (!validateSkillProgress(skillProgress)) {
        return false;
      }
    }
  }

  // Sync metadata (can be null/boolean)
  if (character.lastSyncedAt !== null && typeof character.lastSyncedAt !== 'number') {
    return false;
  }

  if (typeof character.needsSync !== 'boolean') {
    return false;
  }

  return true;
}

/**
 * Validate that an object is a valid SkillProgress.
 *
 * @param data - Unknown data to validate
 * @returns True if data is valid SkillProgress
 */
function validateSkillProgress(data: unknown): data is SkillProgress {
  return isSkillProgress(data);
}

/**
 * Validate that an object is a valid CharacterStat.
 *
 * @param data - Unknown data to validate
 * @returns True if data is valid CharacterStat
 */
function validateCharacterStat(data: unknown): data is CharacterStat {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const stat = data as Record<string, unknown>;

  return (
    typeof stat.statId === 'string' &&
    typeof stat.currentValue === 'number' &&
    (stat.maximumValue === null || typeof stat.maximumValue === 'number')
  );
}

/**
 * Validate that an object is a valid CurrentActivity.
 *
 * @param data - Unknown data to validate
 * @returns True if data is valid CurrentActivity
 */
function validateCurrentActivity(data: unknown): data is CurrentActivity {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const activity = data as Record<string, unknown>;

  const validTypes = ['idle', 'skilling', 'combat', 'traveling'];
  if (typeof activity.type !== 'string' || !validTypes.includes(activity.type)) {
    return false;
  }

  if (typeof activity.startedAt !== 'number' || typeof activity.progress !== 'number') {
    return false;
  }

  // activityData is optional
  if (
    activity.activityData !== undefined &&
    (typeof activity.activityData !== 'object' || activity.activityData === null)
  ) {
    return false;
  }

  return true;
}

/**
 * Validate that an object is a valid CharacterExport.
 *
 * @param data - Unknown data to validate
 * @returns True if data is valid CharacterExport
 */
export function validateCharacterExport(data: unknown): data is CharacterExport {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const exportData = data as Record<string, unknown>;

  // Version must be 1 (current version)
  if (exportData.version !== 1) {
    return false;
  }

  // exportedAt must be a number
  if (typeof exportData.exportedAt !== 'number') {
    return false;
  }

  // character must be valid CharacterData
  if (!validateCharacterData(exportData.character)) {
    return false;
  }

  return true;
}

/**
 * Get a character stat value safely.
 *
 * @param character - The character data
 * @param statId - The stat ID to get
 * @returns The stat's current value, or 0 if not found
 */
export function getCharacterStatValue(character: CharacterData, statId: string): number {
  return character.stats[statId]?.currentValue ?? 0;
}

/**
 * Get a character stat's maximum value safely.
 *
 * @param character - The character data
 * @param statId - The stat ID to get
 * @returns The stat's maximum value, or null if not found or no max
 */
export function getCharacterStatMaximum(character: CharacterData, statId: string): number | null {
  return character.stats[statId]?.maximumValue ?? null;
}

/**
 * Create a CharacterSummary from CharacterData.
 *
 * @param character - The full character data
 * @returns A lightweight summary for UI display
 */
export function createCharacterSummary(
  character: CharacterData
): import('../types/character.js').CharacterSummary {
  return {
    id: character.id,
    name: character.name,
    slotIndex: character.slotIndex,
    lastPlayedAt: character.lastPlayedAt,
    totalPlayTime: character.totalPlayTime,
    needsSync: character.needsSync,
  };
}
